"use client";

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AppImage from '@/components/ui/AppImage';

// ── Intersection Observer hook ──────────────────────────────
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) {setInView(true);obs.disconnect();}},
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

// ── Project list data ────────────────────────────────────────
const projects = [
{ id: '01', name: '城市私宅', nameEn: 'Urban Residence', type: '住宅翻新', year: '2024', img: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=600&q=80&auto=format' },
{ id: '02', name: '日式靜謐茶室', nameEn: 'Japanese Tea Room', type: '商業空間', year: '2024', img: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=600&q=80&auto=format' },
{ id: '03', name: '北歐極簡公寓', nameEn: 'Nordic Minimal Flat', type: '新成屋客變', year: '2023', img: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600&q=80&auto=format' },
{ id: '04', name: '品牌咖啡廳', nameEn: 'Branded Café', type: '商業空間', year: '2023', img: "https://img.rocket.new/generatedImages/rocket_gen_img_1245f2fbf-1773145898802.png" }];


// ── Philosophy pillars ───────────────────────────────────────
const pillars = [
{ no: '01', title: '溫暖優先', titleEn: 'Warmth First', desc: '材質、色調、光線、動線，都以「讓人感到被擁抱」為第一考量。' },
{ no: '02', title: '細節控', titleEn: 'Detail Obsessed', desc: '從隱藏式收納到插座位置，從燈光層次到把手觸感，細節決定舒適度。' },
{ no: '03', title: '永續思維', titleEn: 'Sustainable', desc: '選用環保、耐用、可循環的材料，讓美觀與責任並存。' },
{ no: '04', title: '跨界融合', titleEn: 'Cross-Style', desc: '北歐簡約、日式留白、工業粗獷、現代俐落——只要適合你，我們都嘗試。' }];


export default function Homepage() {
  const heroRef = useRef<HTMLElement>(null);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [hoveredProject, setHoveredProject] = useState<string | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const sec1 = useInView(0.12);
  const sec2 = useInView(0.12);
  const sec3 = useInView(0.12);
  const sec4 = useInView(0.12);

  useEffect(() => {
    const timer = setTimeout(() => setHeroLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleMouseMove = (e: React.MouseEvent) => {
    setMousePos({ x: e.clientX, y: e.clientY });
  };

  return (
    <>
      <Header />

      {/* ── HERO ──────────────────────────────────────────── */}
      <section
        ref={heroRef}
        className="relative min-h-screen flex items-end overflow-hidden bg-primary noise-overlay">
        
        {/* Background image */}
        <div className="absolute inset-0 z-0">
          <AppImage
            src="https://img.rocket.new/generatedImages/rocket_gen_img_10ef3637a-1773145947565.png"
            alt="Anri Design — warm interior living space with natural light"
            fill
            priority
            className="object-cover"
            style={{ opacity: 0.45 }} />
          
          {/* Gradient overlays */}
          <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/80 via-transparent to-transparent" />
        </div>

        {/* Animated grid lines */}
        <div className="absolute inset-0 z-0 pointer-events-none opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
            <defs>
              <linearGradient id="hg1" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#C9A96E" stopOpacity="0" />
                <stop offset="50%" stopColor="#C9A96E" stopOpacity="1" />
                <stop offset="100%" stopColor="#C9A96E" stopOpacity="0" />
              </linearGradient>
            </defs>
            <line x1="25%" y1="0" x2="25%" y2="100%" stroke="rgba(201,169,110,0.3)" strokeWidth="1" />
            <line x1="50%" y1="0" x2="50%" y2="100%" stroke="rgba(201,169,110,0.2)" strokeWidth="1" />
            <line x1="75%" y1="0" x2="75%" y2="100%" stroke="rgba(201,169,110,0.15)" strokeWidth="1" />
          </svg>
        </div>

        {/* Hero content */}
        <div className="relative z-10 w-full max-w-container mx-auto px-6 lg:px-12 pb-20 pt-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            {/* Left: Typography */}
            <div className="lg:col-span-8">
              {/* Tag */}
              <div
                className="flex items-center gap-3 mb-8"
                style={{
                  opacity: heroLoaded ? 1 : 0,
                  transform: heroLoaded ? 'translateY(0)' : 'translateY(20px)',
                  transition: 'opacity 0.8s ease, transform 0.8s ease',
                  transitionDelay: '0.1s'
                }}>
                
                <span className="animate-pulse-dot w-2 h-2 rounded-full bg-accent" />
                <span className="font-mono text-xs text-accent/80 tracking-widest uppercase">
                  Hong Kong Interior Design Studio
                </span>
              </div>

              {/* Main headline */}
              <h1
                className="font-heading font-800 text-white leading-none tracking-tight mb-6"
                style={{
                  fontSize: 'clamp(52px, 8vw, 100px)',
                  opacity: heroLoaded ? 1 : 0,
                  transform: heroLoaded ? 'translateY(0)' : 'translateY(40px)',
                  transition: 'opacity 1s ease, transform 1s ease',
                  transitionDelay: '0.2s'
                }}>
                
                打造屬於你的
                <br />
                <span className="text-gradient-gold">日常儀式感</span>
              </h1>

              <p
                className="font-body text-lg text-white/60 max-w-xl leading-relaxed mb-10"
                style={{
                  opacity: heroLoaded ? 1 : 0,
                  transform: heroLoaded ? 'translateY(0)' : 'translateY(20px)',
                  transition: 'opacity 0.8s ease, transform 0.8s ease',
                  transitionDelay: '0.4s'
                }}>
                
                安日室內設計工作室 · 香港上環信德中心
                <br />
                以人為本，從你的生活習慣出發，設計每個細節。
              </p>

              <div
                className="flex flex-wrap gap-4"
                style={{
                  opacity: heroLoaded ? 1 : 0,
                  transform: heroLoaded ? 'translateY(0)' : 'translateY(20px)',
                  transition: 'opacity 0.8s ease, transform 0.8s ease',
                  transitionDelay: '0.55s'
                }}>
                
                <Link href="/registration-center" className="btn-accent">
                  免費諮詢預約
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>

            {/* Right: Floating glass card */}
            <div
              className="lg:col-span-4 hidden lg:block"
              style={{
                opacity: heroLoaded ? 1 : 0,
                transform: heroLoaded ? 'translateY(0)' : 'translateY(40px)',
                transition: 'opacity 1s ease, transform 1s ease',
                transitionDelay: '0.6s'
              }}>
              
              <div className="glass-dark p-6 animate-float" style={{ borderRadius: '4px' }}>
                <p className="font-mono text-xs text-white/40 tracking-widest uppercase mb-4">
                  Studio Stats · 2025
                </p>
                <div className="space-y-4">
                  {[
                  { label: '完成項目', value: '120+', sub: 'Completed Projects' },
                  { label: '服務年限', value: '8年', sub: 'Years of Service' },
                  { label: '客戶滿意度', value: '98%', sub: 'Client Satisfaction' }].
                  map((stat) =>
                  <div key={stat.label} className="flex items-center justify-between border-b border-white/10 pb-3">
                      <div>
                        <p className="font-body text-xs text-white/50">{stat.label}</p>
                        <p className="font-mono text-[10px] text-white/30">{stat.sub}</p>
                      </div>
                      <span className="font-heading text-2xl font-700 text-accent">{stat.value}</span>
                    </div>
                  )}
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <span className="animate-pulse-dot w-1.5 h-1.5 rounded-full bg-accent" />
                  <span className="font-mono text-[10px] text-white/30 tracking-wide">上環 · 香港</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 opacity-40">
          <span className="font-mono text-[10px] text-white tracking-widest">SCROLL</span>
          <div className="w-px h-12 bg-gradient-to-b from-white/60 to-transparent" />
        </div>
      </section>

      {/* ── SERVICES BENTO ────────────────────────────────── */}
      <section
        ref={sec1.ref as React.RefObject<HTMLElement>}
        className="py-24 bg-background">
        
        <div className="max-w-container mx-auto px-6 lg:px-12">
          {/* Header */}
          <div
            className="mb-16"
            style={{
              opacity: sec1.inView ? 1 : 0,
              transform: sec1.inView ? 'translateY(0)' : 'translateY(40px)',
              transition: 'opacity 0.8s ease, transform 0.8s ease'
            }}>
            
            <span className="tag-pill mb-4 inline-block">Our Services</span>
            <h2 className="font-heading font-700 text-h2 text-primary">
              我們的服務範疇
            </h2>
          </div>

          {/* Bento grid — asymmetric */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Large card: Residential */}
            <div
              className="lg:col-span-2 relative overflow-hidden group hover-shine cursor-pointer"
              style={{
                borderRadius: '4px',
                opacity: sec1.inView ? 1 : 0,
                transform: sec1.inView ? 'translateY(0)' : 'translateY(40px)',
                transition: 'opacity 0.8s ease 0.1s, transform 0.8s ease 0.1s'
              }}>
              
              <div className="relative h-72 md:h-96 overflow-hidden">
                <AppImage
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_13e67fe23-1773145947596.png"
                  alt="Residential interior design — warm living room with natural materials"
                  fill
                  className="object-cover transition-transform duration-700 group-hover:scale-105" />
                
                <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/30 to-transparent" />
                <div className="absolute bottom-0 left-0 p-8">
                  <span className="tag-pill border-accent/40 text-accent mb-3 inline-block">住宅空間</span>
                  <h3 className="font-heading text-2xl font-700 text-white mb-2">Residential Design</h3>
                  <p className="font-body text-sm text-white/60 max-w-sm">
                    新成屋客變、老屋翻新、預售屋客變、系統家具整合
                  </p>
                </div>
              </div>
            </div>

            {/* Small card: Commercial */}
            <div
              className="relative overflow-hidden group hover-shine cursor-pointer"
              style={{
                borderRadius: '4px',
                opacity: sec1.inView ? 1 : 0,
                transform: sec1.inView ? 'translateY(0)' : 'translateY(40px)',
                transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s'
              }}>
              
              <div className="relative h-72 md:h-96 overflow-hidden">
                <AppImage
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_144556702-1773145947560.png"
                  alt="Commercial interior design — modern café space"
                  fill
                  className="object-cover transition-transform duration-700 group-hover:scale-105" />
                
                <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/30 to-transparent" />
                <div className="absolute bottom-0 left-0 p-6">
                  <span className="tag-pill border-accent/40 text-accent mb-3 inline-block">商業空間</span>
                  <h3 className="font-heading text-xl font-700 text-white mb-2">Commercial Design</h3>
                  <p className="font-body text-xs text-white/60">
                    咖啡廳、餐廳、診所、辦公室、品牌展廳
                  </p>
                </div>
              </div>
            </div>

            {/* Bottom stat cards */}
            {[
            { no: '01', title: '以人為本', desc: '傾聽每位屋主的真實需求', icon: '◎' },
            { no: '02', title: '客製化設計', desc: '結合生活習慣，打造專屬空間', icon: '◈' },
            { no: '03', title: '全程陪伴', desc: '從概念到完工，一對一溝通', icon: '◇' }].
            map((item, i) =>
            <div
              key={item.no}
              className="bg-surface p-6 group hover:bg-primary transition-colors duration-300 cursor-pointer"
              style={{
                borderRadius: '4px',
                opacity: sec1.inView ? 1 : 0,
                transform: sec1.inView ? 'translateY(0)' : 'translateY(40px)',
                transition: `opacity 0.8s ease ${0.3 + i * 0.1}s, transform 0.8s ease ${0.3 + i * 0.1}s`
              }}>
              
                <div className="flex items-start justify-between mb-4">
                  <span className="font-mono text-xs text-muted group-hover:text-white/40 transition-colors">{item.no}</span>
                  <span className="text-accent text-2xl">{item.icon}</span>
                </div>
                <h4 className="font-heading text-lg font-600 text-primary group-hover:text-white mb-2 transition-colors">
                  {item.title}
                </h4>
                <p className="font-body text-sm text-secondary group-hover:text-white/60 transition-colors">
                  {item.desc}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── PHILOSOPHY ────────────────────────────────────── */}
      <section
        ref={sec2.ref as React.RefObject<HTMLElement>}
        className="py-24 bg-primary relative overflow-hidden noise-overlay">
        
        {/* Decorative background text */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.03]">
          <span className="font-heading font-800 text-white whitespace-nowrap" style={{ fontSize: '18vw' }}>
            ANRI
          </span>
        </div>

        <div className="relative z-10 max-w-container mx-auto px-6 lg:px-12">
          <div
            className="mb-16"
            style={{
              opacity: sec2.inView ? 1 : 0,
              transform: sec2.inView ? 'translateY(0)' : 'translateY(40px)',
              transition: 'opacity 0.8s ease, transform 0.8s ease'
            }}>
            
            <span className="tag-pill border-white/20 text-white/40 mb-4 inline-block">Design Philosophy</span>
            <h2 className="font-heading font-700 text-h2 text-white">
              設計哲學
            </h2>
          </div>

          {/* 4-column grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {pillars.map((p, i) =>
            <div
              key={p.no}
              className="bg-primary p-8 group hover:bg-white/5 transition-colors duration-300 relative overflow-hidden"
              style={{
                opacity: sec2.inView ? 1 : 0,
                transform: sec2.inView ? 'translateY(0)' : 'translateY(40px)',
                transition: `opacity 0.8s ease ${i * 0.1}s, transform 0.8s ease ${i * 0.1}s`
              }}>
              
                <span className="font-mono text-xs text-white/20 mb-6 block">{p.no}</span>
                <h3 className="font-heading text-xl font-700 text-white mb-1">{p.title}</h3>
                <p className="font-mono text-xs text-accent/70 mb-4 tracking-wide">{p.titleEn}</p>
                <p className="font-body text-sm text-white/50 leading-relaxed">{p.desc}</p>
                {/* Bottom line reveal */}
                <div className="absolute bottom-0 left-0 right-0 h-px bg-accent scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── PORTFOLIO LIST ────────────────────────────────── */}
      <section
        ref={sec3.ref as React.RefObject<HTMLElement>}
        className="py-24 bg-background"
        onMouseMove={handleMouseMove}>
        
        <div className="max-w-container mx-auto px-6 lg:px-12">
          <div
            className="flex flex-col md:flex-row justify-between items-end mb-16"
            style={{
              opacity: sec3.inView ? 1 : 0,
              transform: sec3.inView ? 'translateY(0)' : 'translateY(40px)',
              transition: 'opacity 0.8s ease, transform 0.8s ease'
            }}>
            
            <div>
              <span className="tag-pill mb-4 inline-block">Selected Works</span>
              <h2 className="font-heading font-700 text-h2 text-primary">精選作品</h2>
            </div>
            <Link
              href="/business-scope"
              className="group flex items-center gap-2 font-body text-sm text-secondary hover:text-accent transition-colors mt-4 md:mt-0">
              
              查看全部
              <span className="group-hover:translate-x-1 transition-transform">→</span>
            </Link>
          </div>

          {/* Project list with hover image reveal */}
          <div className="relative">
            {projects.map((project, i) =>
            <div
              key={project.id}
              className="group relative border-t border-border hover:bg-surface transition-colors duration-300"
              onMouseEnter={() => setHoveredProject(project.id)}
              onMouseLeave={() => setHoveredProject(null)}
              style={{
                opacity: sec3.inView ? 1 : 0,
                transform: sec3.inView ? 'translateY(0)' : 'translateY(40px)',
                transition: `opacity 0.8s ease ${i * 0.1}s, transform 0.8s ease ${i * 0.1}s`
              }}>
              
                <div className="flex flex-col md:flex-row md:items-center justify-between px-4 py-8 gap-4">
                  <div className="flex items-center gap-6 md:w-1/2">
                    <span className="font-mono text-xs text-muted group-hover:text-accent transition-colors">{project.id}</span>
                    <div>
                      <h3 className="font-heading text-2xl lg:text-3xl font-500 text-secondary group-hover:text-primary transition-colors group-hover:translate-x-2 duration-300 inline-block">
                        {project.name}
                      </h3>
                      <p className="font-mono text-xs text-muted mt-0.5">{project.nameEn}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-8 md:w-1/2 md:justify-end opacity-50 group-hover:opacity-100 transition-opacity">
                    <span className="font-mono text-xs text-muted">{project.type}</span>
                    <span className="font-mono text-xs text-muted">{project.year}</span>
                    <svg
                    className="w-5 h-5 text-accent -rotate-45 group-hover:rotate-0 transition-transform duration-500"
                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </div>
                </div>

                {/* Hover image */}
                <div
                className="fixed pointer-events-none z-50 w-64 h-44 overflow-hidden shadow-2xl transition-opacity duration-300 hidden lg:block"
                style={{
                  left: mousePos.x + 20,
                  top: mousePos.y - 80,
                  opacity: hoveredProject === project.id ? 1 : 0,
                  borderRadius: '4px'
                }}>
                
                  <AppImage
                  src={project.img}
                  alt={`${project.name} — interior design project`}
                  fill
                  className="object-cover" />
                
                </div>
              </div>
            )}
            <div className="border-t border-border" />
          </div>
        </div>
      </section>

      {/* ── CTA SECTION ───────────────────────────────────── */}
      <section
        ref={sec4.ref as React.RefObject<HTMLElement>}
        className="py-24 bg-surface">
        
        <div className="max-w-container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left */}
            <div
              style={{
                opacity: sec4.inView ? 1 : 0,
                transform: sec4.inView ? 'translateY(0)' : 'translateY(40px)',
                transition: 'opacity 0.8s ease, transform 0.8s ease'
              }}>
              
              <span className="tag-pill mb-6 inline-block">Start Your Project</span>
              <h2 className="font-heading font-700 text-h2 text-primary mb-6">
                準備好打造你的
                <br />
                <span className="text-gradient-gold">理想空間</span> 了嗎？
              </h2>
              <p className="font-body text-base text-secondary leading-relaxed mb-8 max-w-md">
                無論是住宅翻新還是商業空間設計，我們都從你的真實需求出發，
                一起打造屬於你的日常儀式感。
              </p>
              <div className="flex flex-wrap gap-4">
                <Link href="/registration-center" className="btn-accent">
                  立即預約諮詢
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>

            {/* Right: Contact info card */}
            <div
              className="bg-primary p-8 text-white"
              style={{
                borderRadius: '4px',
                opacity: sec4.inView ? 1 : 0,
                transform: sec4.inView ? 'translateY(0)' : 'translateY(40px)',
                transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s'
              }}>
              
              <p className="font-mono text-xs text-white/40 tracking-widest uppercase mb-6">Contact Info</p>
              <div className="space-y-5">
                {[
                {
                  icon:
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>,

                  label: '電郵',
                  value: 'macklavelle14@gmail.com',
                  href: 'mailto:macklavelle14@gmail.com'
                },
                {
                  icon:
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>,

                  label: '地址',
                  value: '香港上環干諾道中 168-200 號信德中心',
                  href: null
                }].
                map((item) =>
                <div key={item.label} className="flex items-start gap-4 group">
                    <div className="text-accent mt-0.5 flex-shrink-0">{item.icon}</div>
                    <div>
                      <p className="font-mono text-xs text-white/30 mb-0.5">{item.label}</p>
                      {item.href ?
                    <a
                      href={item.href}
                      target={item.href.startsWith('http') ? '_blank' : undefined}
                      rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                      className="font-body text-sm text-white/70 hover:text-accent transition-colors">
                      
                          {item.value}
                        </a> :

                    <p className="font-body text-sm text-white/70">{item.value}</p>
                    }
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>);

}